import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Settings, Plus, Pencil, Trash2, RotateCcw, Save, Truck, Wrench, ClipboardCheck, Home } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const APP_INFO = {
  ridero: { name: 'Ridero', icon: Truck, color: 'bg-blue-500' },
  pti_pector: { name: 'PTI Pector', icon: ClipboardCheck, color: 'bg-green-500' },
  mechanic_kiosk: { name: 'Mechanic Kiosk', icon: Wrench, color: 'bg-orange-500' },
  hub: { name: 'Leader Hub', icon: Home, color: 'bg-purple-500' }
};

export default function PointsConfigPage() {
  const { user } = useAuth();
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState('ridero');
  const [editEvent, setEditEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ event_type: '', points: 10, description: '' });
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API}/api/points/config`, { withCredentials: true });
      setConfigs(res.data.apps || {});
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load points configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEvent = async (appId, eventType, points, description) => {
    try {
      await axios.post(`${API}/api/points/config/${appId}/event`, {
        event_type: eventType,
        points: parseInt(points),
        description
      }, { withCredentials: true });
      toast.success(`Updated ${eventType} to ${points} points`);
      fetchConfigs();
      setEditEvent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.event_type) {
      toast.error('Event type is required');
      return;
    }
    try {
      await axios.post(`${API}/api/points/config/${selectedApp}/event`, {
        event_type: newEvent.event_type.toLowerCase().replace(/\s+/g, '_'),
        points: parseInt(newEvent.points),
        description: newEvent.description || newEvent.event_type
      }, { withCredentials: true });
      toast.success(`Added new event: ${newEvent.event_type}`);
      fetchConfigs();
      setShowAddDialog(false);
      setNewEvent({ event_type: '', points: 10, description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add event');
    }
  };

  const handleDeleteEvent = async (appId, eventType) => {
    if (!window.confirm(`Delete "${eventType}" from ${appId}?`)) return;
    try {
      await axios.delete(`${API}/api/points/config/${appId}/event/${eventType}`, { withCredentials: true });
      toast.success(`Deleted ${eventType}`);
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleResetApp = async (appId) => {
    if (!window.confirm(`Reset ${appId} points to defaults? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/api/points/config/${appId}/reset`, {}, { withCredentials: true });
      toast.success(`Reset ${appId} to defaults`);
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset');
    }
  };

  if (user?.role_id !== 'master_admin') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Only Master Admin can configure points</p>
        </div>
      </DashboardLayout>
    );
  }

  const currentConfig = configs[selectedApp] || {};
  const AppIcon = APP_INFO[selectedApp]?.icon || Settings;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Settings className="w-8 h-8" />
              Points Configuration
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure how many points users earn for each action in connected apps
            </p>
          </div>
        </div>

        {/* App Tabs */}
        <Tabs value={selectedApp} onValueChange={setSelectedApp}>
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            {Object.entries(APP_INFO).map(([appId, info]) => {
              const Icon = info.icon;
              return (
                <TabsTrigger key={appId} value={appId} className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{info.name}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.keys(APP_INFO).map(appId => (
            <TabsContent key={appId} value={appId} className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${APP_INFO[appId].color}`}>
                        {(() => { const Icon = APP_INFO[appId].icon; return <Icon className="w-6 h-6 text-white" />; })()}
                      </div>
                      <div>
                        <CardTitle>{APP_INFO[appId].name} Points</CardTitle>
                        <CardDescription>
                          {Object.keys(configs[appId] || {}).length} event types configured
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleResetApp(appId)}>
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset to Defaults
                      </Button>
                      <Button size="sm" onClick={() => { setSelectedApp(appId); setShowAddDialog(true); }}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add Event
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : Object.keys(configs[appId] || {}).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No custom configuration. Using default points.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(configs[appId] || {}).map(([eventType, config]) => (
                        <div 
                          key={eventType}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <div className="flex-1">
                            <div className="font-medium">{eventType.replace(/_/g, ' ')}</div>
                            <div className="text-sm text-muted-foreground">
                              {config.description || eventType}
                            </div>
                          </div>
                          
                          {editEvent?.appId === appId && editEvent?.eventType === eventType ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                className="w-20"
                                value={editEvent.points}
                                onChange={(e) => setEditEvent({...editEvent, points: e.target.value})}
                              />
                              <Button 
                                size="sm"
                                onClick={() => handleUpdateEvent(appId, eventType, editEvent.points, config.description)}
                              >
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setEditEvent(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-lg px-3 py-1">
                                {config.points} pts
                              </Badge>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setEditEvent({ appId, eventType, points: config.points })}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => handleDeleteEvent(appId, eventType)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Add Event Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Event Type</DialogTitle>
              <DialogDescription>
                Add a new event type for {APP_INFO[selectedApp]?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Event Type</Label>
                <Input
                  placeholder="e.g. delivery_completed"
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                />
                <p className="text-xs text-muted-foreground">
                  Use snake_case (e.g., delivery_completed, photo_uploaded)
                </p>
              </div>
              <div className="space-y-2">
                <Label>Points</Label>
                <Input
                  type="number"
                  value={newEvent.points}
                  onChange={(e) => setNewEvent({...newEvent, points: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="What this event means"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAddEvent}>Add Event</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
